'use client';

import { type LayoutNode, type LayoutNodeStyleMap } from 'shop-ui/types';
import { ELEMENT_TYPE, FEATURE_FLAG_KEYS } from 'shop-ui/constants';
import { getElementUlId } from 'app/shop-ui/tools/getElementUlId';
import { combineComponent } from 'shop-ui/tools/combineComponent';
import { GRID_LAYOUT_TYPE } from 'shop-ui/section/Grid/rules';
import i18n from '@shared/i18n';

export const componentName = 'Media';

export enum OFFICIAL_MEDIA_KEY {
    IMAGE = 'official-media-image',
    VIDEO = 'official-media-video',
}

const mediaConfigMap = {
    [OFFICIAL_MEDIA_KEY.IMAGE]: {
        id: '',
        type: ELEMENT_TYPE.IMAGE,
        name: componentName,
        enable: true, // 通常要開啟
        next: null,
        deletable: true, // 通常要開啟
    },
    [OFFICIAL_MEDIA_KEY.VIDEO]: {
        id: '',
        type: ELEMENT_TYPE.VIDEO,
        name: componentName,
        enable: true, // 通常要開啟
        next: null,
        deletable: true, // 通常要開啟
    },
} as {
    [key in OFFICIAL_MEDIA_KEY]: LayoutNode;
};

export const features = [
    FEATURE_FLAG_KEYS.RESIZE_HANDLE,
    FEATURE_FLAG_KEYS.MEDIA_TYPE,
    FEATURE_FLAG_KEYS.MEDIA_IMAGE_URL,
    FEATURE_FLAG_KEYS.MEDIA_VIDEO_URL,
    FEATURE_FLAG_KEYS.MEDIA_ASPECT_RATIO,
    FEATURE_FLAG_KEYS.READONLY_DEFAULT_IMAGE_URL,
    FEATURE_FLAG_KEYS.MEDIA_IMAGE_EXTERNAL_URL,
    FEATURE_FLAG_KEYS.MEDIA_VIDEO_EXTERNAL_URL,
    FEATURE_FLAG_KEYS.MEDIA_ALIGNMENT,
    FEATURE_FLAG_KEYS.BORDER_RADIUS,
    FEATURE_FLAG_KEYS.MEDIA_WIDTH,
    FEATURE_FLAG_KEYS.ENABLE_MEDIA_MASK,
    FEATURE_FLAG_KEYS.MEDIA_MASK_COLOR,
    FEATURE_FLAG_KEYS.ENABLE_LINK,
    FEATURE_FLAG_KEYS.HYPERLINK,
    FEATURE_FLAG_KEYS.SHOULD_OPEN_NEW_WINDOW,
    FEATURE_FLAG_KEYS.MEDIA_AUTO_PLAY,
    FEATURE_FLAG_KEYS.MEDIA_MUTE,
    FEATURE_FLAG_KEYS.ANIMATION,
];

const defaultStyleMap = {
    [OFFICIAL_MEDIA_KEY.IMAGE]: {
        [FEATURE_FLAG_KEYS.MEDIA_TYPE]: 'image',
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '0',
        [FEATURE_FLAG_KEYS.MEDIA_WIDTH]: '100%',
        [FEATURE_FLAG_KEYS.MEDIA_MASK_COLOR]: 'rgba(0, 0, 0, 0)',
        [FEATURE_FLAG_KEYS.MEDIA_ALIGNMENT]: 'center',
    },
    [OFFICIAL_MEDIA_KEY.VIDEO]: {
        [FEATURE_FLAG_KEYS.MEDIA_TYPE]: 'video',
        [FEATURE_FLAG_KEYS.BORDER_RADIUS]: '0',
        [FEATURE_FLAG_KEYS.MEDIA_WIDTH]: '100%',
        [FEATURE_FLAG_KEYS.MEDIA_MASK_COLOR]: 'rgba(0, 0, 0, 0)',
        [FEATURE_FLAG_KEYS.MEDIA_ALIGNMENT]: 'center',
    },
} as const;

export function createMedia(name: string, presetStyle?: LayoutNodeStyleMap[string]) {
    const id = getElementUlId();

    const config = mediaConfigMap[name];

    const layout = {
        ...config,
        id,
    };

    const defaultStyle = defaultStyleMap[name];

    return {
        id,
        layout,
        style: {
            ...defaultStyle,
            ...presetStyle,
        },
    };
}

export function generate(
    sectionName: string = i18n.gettext('區塊'),
    name: string = OFFICIAL_MEDIA_KEY.IMAGE,
    options?: { shouldWrap?: boolean }
) {
    const media = createMedia(name);

    const shouldWrap = options?.shouldWrap ?? true;

    return combineComponent(sectionName, [media], {
        primaryGridType: GRID_LAYOUT_TYPE.SINGLE_CELL,
        secondaryGridType: GRID_LAYOUT_TYPE.SINGLE_CELL,
        shouldWrap,
    });
}
